import Skeleton from "@galaxy-io/dls/feedback/Skeleton";
import Icon from "@galaxy-io/dls/icons/Icon";
import Box from "@galaxy-io/dls/layout/Box";
import Flex, { AlignItems, FlexDirection, JustifyContent } from "@galaxy-io/dls/layout/Flex";
import Text, { TextSize, TextVariant, TextWeight } from "@galaxy-io/dls/text/Text";
import { FontFamily } from "@galaxy-io/dls/theme/enums";
import { t } from "@galaxy-io/dls/theme/tokens/t";
import Widget, { WidgetSize } from "@galaxy-io/dls/widget/Widget";
import type { Integration } from "@gtm/contracts";
import { styled } from "@linaria/react";
import { ArrowRightIcon, PlusIcon } from "@phosphor-icons/react";
import { ActorLabel, useRecordAudit } from "@/components/workspace/record-audit";
import { SourceIcon } from "@/components/workspace/source-icon";
import { Status } from "@/components/workspace/status";

const Sections = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${t.space[32]};
  padding: ${t.space[24]} var(--gtm-page-inset, ${t.space[24]});
`;
const Cards = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(min(300px, 100%), 1fr));
  align-items: stretch;
  gap: ${t.space[16]};
`;
const Mark = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: 48px;
  height: 48px;
  border: 0.5px solid ${t.color.border.primary};
  border-radius: ${t.radius.md};
  background: ${t.color.background.base};
`;
const Card = styled(Widget)`
  min-width: 0;
  height: 100%;
`;
export const providerNames = {
  github: "GitHub",
  reddit: "Reddit",
  slack: "Slack",
  webhook: "Webhook",
  anthropic: "Anthropic",
  posthog: "PostHog",
} as const;
export const providers = Object.keys(providerNames) as Array<keyof typeof providerNames>;
const descriptions: Record<string, string> = {
  github: "Bring repository activity and developer signals into your workspace.",
  reddit: "Follow subreddit conversations and discover relevant community signals.",
  slack: "Deliver notifications and workflow messages to your Slack channels.",
  webhook: "Send workspace events to your own tools and services.",
  anthropic: "Use Claude to power AI steps in your workflows.",
  posthog: "Connect product analytics to understand how people use your product.",
};

function CardIdentity({ provider, name }: { provider: string; name: string }) {
  return (
    <Flex alignItems={AlignItems.CENTER} gap={12} minWidth={0}>
      <Mark>
        <SourceIcon source={provider} size={24} />
      </Mark>
      <Flex direction={FlexDirection.COLUMN} gap={4} minWidth={0}>
        <Text size={TextSize.HEADING_SM} weight={TextWeight.MEDIUM} lineClamp={1}>
          {name}
        </Text>
        <Text size={TextSize.BODY_SM} variant={TextVariant.SECONDARY}>
          {providerNames[provider as keyof typeof providerNames] || provider}
        </Text>
      </Flex>
    </Flex>
  );
}

export function IntegrationCards({
  connections,
  onManage,
}: {
  connections: Integration[];
  onManage: (id: string) => void;
}) {
  const audit = useRecordAudit(
    "integration",
    connections.map((connection) => connection.id),
  );
  const available = providers.filter(
    (provider) => !connections.some((connection) => connection.provider === provider),
  );
  return (
    <Sections>
      {!!connections.length && (
        <Flex as="section" direction={FlexDirection.COLUMN} gap={16} aria-label="Your connections">
          <Flex direction={FlexDirection.COLUMN} gap={4}>
            <Text as="h2" size={TextSize.BODY_LG} weight={TextWeight.MEDIUM}>
              Your connections · {connections.length}
            </Text>
            <Text size={TextSize.BODY_SM} variant={TextVariant.SECONDARY}>
              Manage connected accounts and their settings.
            </Text>
          </Flex>
          <Cards>
            {connections.map((connection) => (
              <Card
                key={connection.id}
                size={WidgetSize.LARGE}
                isInteractive
                ariaLabel={`Open ${connection.name}`}
                onClick={() => onManage(connection.id)}
                footer={
                  <Flex
                    alignItems={AlignItems.CENTER}
                    justifyContent={JustifyContent.SPACE_BETWEEN}
                    gap={12}
                    width="100%"
                  >
                    <Status value={connection.enabled ? "connected" : "inactive"} />
                    <Flex alignItems={AlignItems.CENTER} gap={8}>
                      <Text size={TextSize.BODY_SM}>Manage</Text>
                      <Icon component={ArrowRightIcon} size={16} />
                    </Flex>
                  </Flex>
                }
              >
                <CardIdentity provider={connection.provider} name={connection.name} />
                <Text
                  size={TextSize.BODY_SM}
                  family={FontFamily.MONO}
                  variant={TextVariant.SECONDARY}
                  lineClamp={1}
                >
                  {connection.account || "No account specified"}
                </Text>
                <Flex alignItems={AlignItems.CENTER} gap={8} minWidth={0}>
                  <Text size={TextSize.BODY_SM} variant={TextVariant.SECONDARY}>
                    Added by
                  </Text>
                  {audit.isPending ? (
                    <Box width={100}>
                      <Skeleton />
                    </Box>
                  ) : (
                    <ActorLabel
                      actor={audit.data?.find((actor) => actor.id === connection.id)?.createdBy}
                    />
                  )}
                </Flex>
              </Card>
            ))}
          </Cards>
        </Flex>
      )}
      {!!available.length && (
        <Flex
          as="section"
          direction={FlexDirection.COLUMN}
          gap={16}
          aria-label="Available integrations"
        >
          <Flex direction={FlexDirection.COLUMN} gap={4}>
            <Text as="h2" size={TextSize.BODY_LG} weight={TextWeight.MEDIUM}>
              Explore integrations
            </Text>
            <Text size={TextSize.BODY_SM} variant={TextVariant.SECONDARY}>
              Connect the tools that power your workspace.
            </Text>
          </Flex>
          <Cards>
            {available.map((provider) => (
              <Card
                key={provider}
                size={WidgetSize.LARGE}
                isInteractive
                ariaLabel={`Connect ${providerNames[provider]}`}
                onClick={() => onManage(`new-${provider}`)}
                footer={
                  <Flex
                    alignItems={AlignItems.CENTER}
                    justifyContent={JustifyContent.SPACE_BETWEEN}
                    width="100%"
                  >
                    <Text size={TextSize.BODY_SM}>Connect {providerNames[provider]}</Text>
                    <Icon component={PlusIcon} size={16} />
                  </Flex>
                }
              >
                <Flex alignItems={AlignItems.CENTER} gap={12}>
                  <Mark>
                    <SourceIcon source={provider} size={24} />
                  </Mark>
                  <Text size={TextSize.HEADING_SM} weight={TextWeight.MEDIUM}>
                    {providerNames[provider]}
                  </Text>
                </Flex>
                <Box minHeight={48}>
                  <Text size={TextSize.BODY_MD} variant={TextVariant.SECONDARY}>
                    {descriptions[provider]}
                  </Text>
                </Box>
              </Card>
            ))}
          </Cards>
        </Flex>
      )}
    </Sections>
  );
}
